/**
 * Canvas-based confetti animation that's more performant than DOM-based approach.
 */

// Confetti particle class
class Particle {
  constructor(canvas, colors, angle = 0) {
    this.canvas = canvas;
    this.context = canvas.getContext('2d');
    
    // Random properties for variety
    this.color = colors[Math.floor(Math.random() * colors.length)];
    this.size = Math.random() * 8 + 4; // 4-12px - slightly smaller for more delicate gold appearance
    this.initialAngle = angle || Math.random() * Math.PI * 2;
    this.angle = this.initialAngle;
    this.rotationSpeed = (Math.random() - 0.5) * 0.2;
    this.type = Math.floor(Math.random() * 4); // 0: square, 1: circle, 2: triangle, 3: star
    
    // Starting position and velocity - will be set later
    this.x = 0;
    this.y = 0;
    
    // Velocity will now be set with an omnidirectional explosion pattern
    this.velocity = { x: 0, y: 0 };
    this.gravity = 0.3; // Slightly reduced gravity for longer hang time
    this.friction = 0.97; // Slightly less friction for smoother movement
    this.opacity = 1;
    this.opacityDecay = Math.random() * 0.03 + 0.02;
    
    // How long the particle should live (in frames)
    this.life = 100 + Math.random() * 50;
    this.remainingLife = this.life;
  }
  
  update() {
    // Apply physics to update position
    this.velocity.x *= this.friction;  // Apply horizontal friction
    this.velocity.y += this.gravity;   // Apply gravity
    this.velocity.y *= this.friction;  // Apply vertical friction too for more natural movement
    
    // Update position based on velocity
    this.x += this.velocity.x;
    this.y += this.velocity.y;
    
    // Update rotation - make rotation speed proportional to velocity for more dynamic effect
    this.angle += this.rotationSpeed * (Math.abs(this.velocity.x) + Math.abs(this.velocity.y)) / 10;
    
    // Update opacity based on life
    this.remainingLife--;
    this.opacity = this.remainingLife / this.life;
    
    // Return true if particle is still alive
    return this.remainingLife > 0;
  }
  
  draw() {
    const ctx = this.context;
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.angle);
    
    // Add a subtle gold shimmer effect with gradient (less bright)
    if (Math.random() > 0.8) {  // Only apply to 20% of particles
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, this.size);
      gradient.addColorStop(0, this.color);
      gradient.addColorStop(0.7, this.color);
      
      // Use a slightly golden white rather than pure white
      gradient.addColorStop(1, '#FFFCEB'); // Creamy white with gold tint
      ctx.fillStyle = gradient;
    } else {
      ctx.fillStyle = this.color;
    }
    
    ctx.globalAlpha = this.opacity;
    
    // Draw different shapes based on type
    if (this.type === 0) { // Square
      ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);
    } else if (this.type === 1) { // Circle
      ctx.beginPath();
      ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else if (this.type === 2) { // Triangle
      ctx.beginPath();
      ctx.moveTo(0, -this.size / 2);
      ctx.lineTo(-this.size / 2, this.size / 2);
      ctx.lineTo(this.size / 2, this.size / 2);
      ctx.closePath();
      ctx.fill();
    } else { // Star
      const spikes = 5;
      const outerRadius = this.size / 2;
      const innerRadius = this.size / 4;
      let rot = (Math.PI / 2) * 3;
      const step = Math.PI / spikes;
      
      ctx.beginPath();
      for (let i = 0; i < spikes; i++) {
        const x1 = Math.cos(rot) * outerRadius;
        const y1 = Math.sin(rot) * outerRadius;
        ctx.lineTo(x1, y1);
        rot += step;
        
        const x2 = Math.cos(rot) * innerRadius;
        const y2 = Math.sin(rot) * innerRadius;
        ctx.lineTo(x2, y2);
        rot += step;
      }
      ctx.closePath();
      ctx.fill();
    }
    
    ctx.restore();
  }
}

/**
 * Create and animate confetti using canvas
 * @param {HTMLElement} container - Element to append canvas to
 * @param {number} count - Number of confetti particles
 * @param {Object} sourceElement - Element to position confetti around
 */
export const createCanvasConfetti = (container, count = 50, sourceElement = null) => {
  // Create canvas element
  const canvas = document.createElement('canvas');
  canvas.style.position = 'fixed'; // Use fixed positioning to follow viewport
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = '1000'; // High z-index to be above everything
  canvas.style.top = '0';
  canvas.style.left = '0';
  canvas.style.width = '100vw';
  canvas.style.height = '100vh';
  
  // Set canvas size
  const setCanvasSize = () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  };
  setCanvasSize();
  
  // Calculate the origin point for confetti
  let originX, originY;
  
  if (sourceElement) {
    const rect = sourceElement.getBoundingClientRect();
    
    // Center point for particles - use viewport coordinates since canvas is fixed
    // Get fresh coordinates in case the element has moved
    const freshRect = sourceElement.getBoundingClientRect();
    originX = freshRect.left + freshRect.width / 2;
    originY = freshRect.top + freshRect.height / 2;
    
    // For pill badges, position slightly higher for better visual effect
    if (sourceElement.classList.contains('rounded-full')) {
      originY = freshRect.top + (freshRect.height * 0.5);
    }
    
    console.log('Badge position:', freshRect.top, freshRect.left, freshRect.width, freshRect.height);
    console.log('Setting origin to:', originX, originY);
  } else {
    // Default to center of screen if no source element
    originX = window.innerWidth / 2;
    originY = window.innerHeight / 2;
  }
  
  // Append canvas to container
  container.appendChild(canvas);
  
  // Balanced gold color palette - mix of rich gold and some brighter highlights
  const colors = [
    '#FFD700', // Classic gold
    '#FDCC0D', // Slightly darker gold
    '#F1C40F', // Darker gold with slight amber
    '#FFC125', // Deep gold
    '#EEC900', // Goldenrod
    '#DAA520', // Golden/amber hybrid
    '#FFD54F', // Mid-bright gold
    '#FFECB3', // Light gold (only a few bright pieces)
    '#FFC107', // Gold with slight orange
    '#FFF2CC', // Very light gold (rare bright highlight)
  ];
  
  // Create particles - balanced amount for a proper celebration effect
  const particles = [];
  const actualCount = Math.floor(count * 0.81); // Reduce count by 19% (10% less than previous 10% reduction)
  for (let i = 0; i < actualCount; i++) {
    // Use full 360° range for omnidirectional explosion
    const angle = Math.random() * Math.PI * 2; // 0° to 360° range
    particles.push(new Particle(canvas, colors, angle));
  }
  
  // Set initial positions and velocities for an omnidirectional explosion
  particles.forEach((particle, index) => {
    // Use the origin point we calculated earlier
    particle.x = originX;
    particle.y = originY;
    
    // Create explosion velocity pattern - balanced initial speed (not too weak, not too strong)
    const speed = 3.24 + Math.random() * 6.48; // 10% less power: speed between 3.24 and 9.72
    
    // For better distribution, distribute some particles at specific angles
    let angle;
    if (index < 8) {
      // First 8 particles get evenly distributed around the circle
      angle = (index / 8) * Math.PI * 2;
    } else {
      // Rest get random angles
      angle = Math.random() * Math.PI * 2; // Random angle in radians (0 to 2π)
    }
    
    // Set velocity components based on angle and speed
    particle.velocity.x = Math.cos(angle) * speed;
    particle.velocity.y = Math.sin(angle) * speed;
    
    // Add slight upward bias for a more pleasing effect (helps counteract gravity)
    if (particle.velocity.y > 0) {
      particle.velocity.y *= 0.75; // More reduction in downward velocity for better upward distribution
    }
  });
  
  // Animation loop
  let animationFrameId;
  
  const animate = () => {
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Update and draw particles
    for (let i = particles.length - 1; i >= 0; i--) {
      const particle = particles[i];
      const alive = particle.update();
      
      if (alive) {
        particle.draw();
      } else {
        particles.splice(i, 1);
      }
    }
    
    // Continue animation if particles remain, otherwise clean up
    if (particles.length > 0) {
      animationFrameId = requestAnimationFrame(animate);
    } else {
      container.removeChild(canvas);
    }
  };
  
  // Start animation
  animate();
  
  // Handle window resize
  const handleResize = () => {
    setCanvasSize();
  };
  window.addEventListener('resize', handleResize);
  
  // Clean up function (could be called if needed)
  return () => {
    window.removeEventListener('resize', handleResize);
    cancelAnimationFrame(animationFrameId);
    if (container.contains(canvas)) {
      container.removeChild(canvas);
    }
  };
};

export default createCanvasConfetti;